import React, { useState, useEffect, useContext, useRef, useMemo, useImperativeHandle } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

// --- Audio recorder (estable) con MediaRecorder ---
const pickAudioMimeType = () => {
  if (window.MediaRecorder && MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
    return "audio/ogg;codecs=opus";
  }
  if (window.MediaRecorder && MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  return "";
};

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },
  newMessageBox: {
    background: "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },
  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },
  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },
  sendMessageIcons: { color: "grey" },
  uploadInput: { display: "none" },
  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },
  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },
  audioLoading: { color: green[500], opacity: "70%" },
  recorderWrapper: { display: "flex", alignItems: "center", alignContent: "middle" },
  cancelAudioIcon: { color: "red" },
  sendAudioIcon: { color: "green" },
  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },
  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },
  replyginContactMsgSideColor: { flex: "none", width: "4px", backgroundColor: "#35cd96" },
  replyginSelfMsgSideColor: { flex: "none", width: "4px", backgroundColor: "#6bcbef" },
  messageContactName: { display: "flex", color: "#6bcbef", fontWeight: 500 },
  dropOverlay: {
    position: "absolute",
    inset: 0,
    borderRadius: 20,
    background: "rgba(0,0,0,0.04)",
    border: "2px dashed rgba(0,0,0,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    fontSize: 14,
    color: "rgba(0,0,0,0.65)",
    zIndex: 2,
  },
  messageInputWrapperDragActive: {
    outline: "2px dashed rgba(0,0,0,0.25)",
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: "#ffffff",
    padding: "2px",
    border: "1px solid #CCC",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": { background: "#F1F1F1", cursor: "pointer" },
      },
    },
  },
}));

// CAMBIO 1: acepta ticketId como prop (para el chat del Kanban)
const MessageInput = React.forwardRef(({ ticketStatus, ticketId: ticketIdProp }, ref) => {
  const classes = useStyles();
  const { ticketId: ticketIdParam } = useParams();
  const ticketId = ticketIdProp || ticketIdParam;  // usa prop si existe, si no usa URL

  const [medias, setMedias] = useState([]);
  const isImage = (file) => file?.type?.startsWith("image/");
  const isPdf = (file) => file?.type === "application/pdf";

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);

  const prettySize = (bytes = 0) => {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const previews = useMemo(() => {
    if (!medias?.length) return [];
    return medias.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      image: isImage(file),
      pdf: isPdf(file),
    }));
  }, [medias]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
    };
  }, [previews]);

  const removeMediaAt = (idx) => {
    setMedias((prev) => prev.filter((_, i) => i !== idx));
  };

  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const canDrop = ticketStatus === "open" && !loading && !recording;

  const handleDropFiles = (files) => {
    if (!files || files.length === 0) return;
    setMedias((prev) => [...prev, ...Array.from(files)]);
  };

  // Permite que el contenedor del chat (Ticket/Kanban) reenvíe archivos
  // soltados en cualquier parte de la ventana, no solo sobre el textarea.
  useImperativeHandle(ref, () => ({ handleDropFiles }));

  const handleDragOver = (e) => { if (!canDrop) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; };
  const handleDragEnter = (e) => { if (!canDrop) return; e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!canDrop) return; e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setIsDragging(false); };
  const handleDrop = (e) => { if (!canDrop) return; e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (e.dataTransfer?.files?.length) handleDropFiles(e.dataTransfer.files); };

  const { setReplyingMessage, replyingMessage } = useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => { inputRef.current.focus(); }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    return () => {
      try {
        audioChunksRef.current = [];
        if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current.stop();
        mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
      } catch {}
      mediaRecorderRef.current = null;
      mediaStreamRef.current = null;
    };
  }, [ticketId]);

  const handleChangeInput = (e) => { setInputMessage(e.target.value); handleLoadQuickAnswer(e.target.value); };

  // Si la respuesta rápida trae un adjunto (audio/video/imagen/documento),
  // lo baja del servidor y lo deja listo en el mismo panel de vista previa
  // que usa "adjuntar archivo a mano" — así se revisa/envía igual que
  // cualquier adjunto, sin tener que ir a buscarlo entre los archivos.
  const handleQuickAnswersClick = async (value) => {
    setTypeBar(false);
    if (value.mediaPath) {
      setLoading(true);
      try {
        const response = await fetch(`${getBackendUrl()}/public/${value.mediaPath}`);
        if (!response.ok) {
          throw new Error(`No se pudo descargar el adjunto (HTTP ${response.status})`);
        }
        const blob = await response.blob();
        const file = new File(
          [blob],
          value.mediaOriginalName || value.mediaPath,
          { type: value.mediaMimetype || blob.type }
        );
        setMedias([file]);
      } catch (err) {
        toast.error(err.message || "No se pudo adjuntar el archivo de la respuesta rápida.");
      } finally {
        setLoading(false);
      }
    }
    setInputMessage(value.message || "");
  };
  const handleAddEmoji = (e) => { setInputMessage((prev) => prev + e.native); };
  const handleChangeMedias = (e) => { if (!e.target.files) return; setMedias(Array.from(e.target.files)); };
  const handleInputPaste = (e) => { if (e.clipboardData.files[0]) setMedias([e.clipboardData.files[0]]); };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();
    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => { formData.append("medias", media); formData.append("body", media.name); });
    try { await api.post(`/messages/${ticketId}`, formData); } catch (err) { toastError(err); }
    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);
    const message = {
      read: 1, fromMe: true, mediaUrl: "",
      body: signMessage ? `*${user?.name}:*\n${inputMessage.trim()}` : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try { await api.post(`/messages/${ticketId}`, message); } catch (err) { toastError(err); }
    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Tu navegador no soporta grabación de audio.");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onerror = (e) => { console.error("Recorder error:", e); };
      recorder.onstop = () => {
        try { mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch {}
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", { params: { searchParam: inputMessage.substring(1) } });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch (err) { setTypeBar(false); }
    } else { setTypeBar(false); }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const recorder = mediaRecorderRef.current;
      if (!recorder) throw new Error("No hay grabación activa.");

      const stopped = new Promise((resolve) => {
        const prevOnStop = recorder.onstop;
        recorder.onstop = (ev) => {
          try { prevOnStop && prevOnStop(ev); } catch {}
          resolve(true);
        };
      });

      recorder.stop();
      await stopped;

      const rawMimeType = recorder.mimeType || pickAudioMimeType() || "audio/webm";
      const normalizedMimeType = rawMimeType.startsWith("audio/ogg")
        ? "audio/ogg"
        : rawMimeType.startsWith("audio/webm")
        ? "audio/webm"
        : rawMimeType;

      const blob = new Blob(audioChunksRef.current, { type: normalizedMimeType });

      if (!blob || blob.size < 2000) {
        setRecording(false);
        return;
      }

      // CAMBIO 2: ya no bloqueamos WEBM — el backend convierte con ffmpeg
      const ext = normalizedMimeType.includes("ogg") ? "ogg" : "webm";
      const filename = `${Date.now()}.${ext}`;

      const file = new File([blob], filename, { type: normalizedMimeType });
      const formData = new FormData();
      formData.append("medias", file);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);

      setRecording(false);
    } catch (err) {
      toastError(err);
      setRecording(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAudio = async () => {
    try {
      const recorder = mediaRecorderRef.current;
      audioChunksRef.current = [];
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      } else {
        mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
      setRecording(false);
    }
  };

  const handleOpenMenuClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleMenuItemClick = (event) => { setAnchorEl(null); };

  const renderReplyingMessage = (message) => (
    <div className={classes.replyginMsgWrapper}>
      <div className={classes.replyginMsgContainer}>
        <span className={clsx(classes.replyginContactMsgSideColor, { [classes.replyginSelfMsgSideColor]: !message.fromMe })}></span>
        <div className={classes.replyginMsgBody}>
          {!message.fromMe && <span className={classes.messageContactName}>{message.contact?.name}</span>}
          {message.body}
        </div>
      </div>
      <IconButton aria-label="showRecorder" component="span"
        disabled={loading || ticketStatus !== "open"} onClick={() => setReplyingMessage(null)}>
        <ClearIcon className={classes.sendMessageIcons} />
      </IconButton>
    </div>
  );

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton aria-label="cancel-upload" component="span" onClick={() => setMedias([])}>
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>
        {loading ? (
          <div><CircularProgress className={classes.circleLoading} /></div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
            {previews.map((p, idx) => (
              <div key={`${p.file.name}-${idx}`}
                style={{ width: 110, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 10, overflow: "hidden", background: "#fff", position: "relative" }}
                title={p.file.name}>
                {p.image ? (
                  <img src={p.url} alt={p.file.name} style={{ width: "100%", height: 70, objectFit: "cover", display: "block" }} />
                ) : (
                  <div style={{ height: 70, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                    {p.pdf ? "PDF" : "ARCHIVO"}
                  </div>
                )}
                <div style={{ padding: 6, fontSize: 12 }}>
                  <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.file.name}</div>
                  <div style={{ opacity: 0.7, fontSize: 11 }}>{prettySize(p.file.size)}</div>
                </div>
                <button type="button" onClick={() => removeMediaAt(idx)}
                  style={{ position: "absolute", top: 6, right: 6, border: "none", borderRadius: 999, width: 22, height: 22, cursor: "pointer", background: "rgba(0,0,0,0.55)", color: "#fff", lineHeight: "22px" }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <IconButton aria-label="send-upload" component="span" onClick={handleUploadMedia} disabled={loading}>
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
    );

  return (
    <Paper square elevation={0} className={classes.mainWrapper}>
      {replyingMessage && renderReplyingMessage(replyingMessage)}
      <div className={classes.newMessageBox}>
        <Hidden only={["sm", "xs"]}>
          <IconButton aria-label="emojiPicker" component="span"
            disabled={loading || recording || ticketStatus !== "open"}
            onClick={(e) => setShowEmoji((prev) => !prev)}>
            <MoodIcon className={classes.sendMessageIcons} />
          </IconButton>
          {showEmoji && (
            <div className={classes.emojiBox}>
              <ClickAwayListener onClickAway={(e) => setShowEmoji(false)}>
                <Picker perLine={16} showPreview={false} showSkinTones={false} onSelect={handleAddEmoji} />
              </ClickAwayListener>
            </div>
          )}
          <input multiple type="file" id="upload-button-desktop"
            disabled={loading || recording || ticketStatus !== "open"}
            className={classes.uploadInput} onChange={handleChangeMedias} />
          <label htmlFor="upload-button-desktop">
            <IconButton aria-label="upload" component="span" disabled={loading || recording || ticketStatus !== "open"}>
              <AttachFileIcon className={classes.sendMessageIcons} />
            </IconButton>
          </label>
          <FormControlLabel
            style={{ marginRight: 7, color: "gray" }}
            label={i18n.t("messagesInput.signMessage")}
            labelPlacement="start"
            control={<Switch size="small" checked={signMessage} onChange={(e) => setSignMessage(e.target.checked)} name="showAllTickets" color="primary" />}
          />
        </Hidden>
        <Hidden only={["md", "lg", "xl"]}>
          <IconButton aria-controls="simple-menu" aria-haspopup="true" onClick={handleOpenMenuClick}>
            <MoreVert />
          </IconButton>
          <Menu id="simple-menu" keepMounted anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuItemClick}>
            <MenuItem onClick={handleMenuItemClick}>
              <IconButton aria-label="emojiPicker" component="span"
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={(e) => setShowEmoji((prev) => !prev)}>
                <MoodIcon className={classes.sendMessageIcons} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={handleMenuItemClick}>
              <input multiple type="file" id="upload-button-mobile"
                disabled={loading || recording || ticketStatus !== "open"}
                className={classes.uploadInput} onChange={handleChangeMedias} />
              <label htmlFor="upload-button-mobile">
                <IconButton aria-label="upload" component="span" disabled={loading || recording || ticketStatus !== "open"}>
                  <AttachFileIcon className={classes.sendMessageIcons} />
                </IconButton>
              </label>
            </MenuItem>
            <MenuItem onClick={handleMenuItemClick}>
              <FormControlLabel
                style={{ marginRight: 7, color: "gray" }}
                label={i18n.t("messagesInput.signMessage")}
                labelPlacement="start"
                control={<Switch size="small" checked={signMessage} onChange={(e) => setSignMessage(e.target.checked)} name="showAllTickets" color="primary" />}
              />
            </MenuItem>
          </Menu>
        </Hidden>
        <div
          className={clsx(classes.messageInputWrapper, { [classes.messageInputWrapperDragActive]: isDragging })}
          onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          {isDragging && <div className={classes.dropOverlay}>Suelta el archivo aquí para adjuntarlo</div>}
          <InputBase
            inputRef={(input) => { input && input.focus(); input && (inputRef.current = input); }}
            className={classes.messageInput}
            placeholder={ticketStatus === "open" ? i18n.t("messagesInput.placeholderOpen") : i18n.t("messagesInput.placeholderClosed")}
            multiline maxRows={5} value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || ticketStatus !== "open"}
            onPaste={(e) => { ticketStatus === "open" && handleInputPaste(e); }}
            onKeyPress={(e) => { if (loading || e.shiftKey) return; if (e.key === "Enter") handleSendMessage(); }}
          />
          {typeBar ? (
            <ul className={classes.messageQuickAnswersWrapper}>
              {quickAnswers.map((value, index) => (
                <li className={classes.messageQuickAnswersWrapperItem} key={index}>
                  <a onClick={() => handleQuickAnswersClick(value)}>
                    {value.mediaPath ? "📎 " : ""}
                    {value.shortcut}
                    {value.message ? ` - ${value.message}` : ""}
                  </a>
                </li>
              ))}
            </ul>
          ) : <div></div>}
        </div>
        {inputMessage ? (
          <IconButton aria-label="sendMessage" component="span" onClick={handleSendMessage} disabled={loading}>
            <SendIcon className={classes.sendMessageIcons} />
          </IconButton>
        ) : recording ? (
          <div className={classes.recorderWrapper}>
            <IconButton aria-label="cancelRecording" component="span" fontSize="large" disabled={loading} onClick={handleCancelAudio}>
              <HighlightOffIcon className={classes.cancelAudioIcon} />
            </IconButton>
            {loading ? <div><CircularProgress className={classes.audioLoading} /></div> : <RecordingTimer />}
            <IconButton aria-label="sendRecordedAudio" component="span" onClick={handleUploadAudio} disabled={loading}>
              <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
            </IconButton>
          </div>
        ) : (
          <IconButton aria-label="showRecorder" component="span"
            disabled={loading || ticketStatus !== "open"} onClick={handleStartRecording}>
            <MicIcon className={classes.sendMessageIcons} />
          </IconButton>
        )}
      </div>
    </Paper>
  );
});

export default MessageInput;